import type {
  ForgeAccountView,
  ForgeHostView,
  ForgeProvider,
  LinkMethod,
} from '@/lib/api/generated/model';

/**
 * A person's linked forge accounts (spec 159), as the Commits page shows them.
 * Pure: no React, no client.
 */

const FORGE_NAME: Record<ForgeProvider, string> = {
  gitlab: 'GitLab',
  github: 'GitHub',
};

export function forgeName(provider: ForgeProvider): string {
  return FORGE_NAME[provider];
}

/** One row of the panel: a host the organization connects to, and the link. */
export interface AccountRow {
  provider: ForgeProvider;
  host: string;
  account: ForgeAccountView | null;
  /** A link for a host no workspace connects to any more. */
  orphan: boolean;
  /** The person can link by signing in on the forge (spec 161). */
  oauthAvailable: boolean;
}

/**
 * Every host the organization connects to, in the service's order, each with
 * the person's link if they have one; then their links for hosts no longer
 * listed, so they can still be seen and removed.
 */
export function accountRows(
  hosts: readonly ForgeHostView[],
  accounts: readonly ForgeAccountView[],
): AccountRow[] {
  const byHost = new Map(accounts.map((account) => [account.host, account]));
  const rows: AccountRow[] = hosts.map((host) => ({
    provider: host.provider,
    host: host.host,
    account: byHost.get(host.host) ?? null,
    orphan: false,
    oauthAvailable: host.oauth_available === true,
  }));
  const listed = new Set(hosts.map((host) => host.host));
  for (const account of accounts) {
    if (!listed.has(account.host)) {
      rows.push({
        provider: account.provider,
        host: account.host,
        account,
        orphan: true,
        oauthAvailable: false,
      });
    }
  }
  return rows;
}

/**
 * A link's state in words; the reason when it is not working. Takes only what
 * it reads, so the admins' people table (spec 160) says the same words.
 */
export function statusWords(
  account: Pick<ForgeAccountView, 'status' | 'status_reason' | 'provider'>,
): string {
  const forge = forgeName(account.provider);
  switch (account.status) {
    case 'working':
      return 'Working';
    case 'expired':
      return 'Expired';
    case 'broken':
      return account.status_reason === 'unreadable'
        ? 'Broken: the token could not be read'
        : `Broken: ${forge} rejected the token`;
    case 'revoked':
      return account.status_reason === 'inactive_owner'
        ? 'Removed: no longer active'
        : 'Removed by an administrator';
  }
}

/**
 * How a link was made (spec 161). A service from before signing in sends no
 * method, and every link it has was made with a token.
 */
export function linkMethodOf(link: { link_method?: LinkMethod }): LinkMethod {
  return link.link_method ?? 'token';
}

const METHOD_WORDS: Record<LinkMethod, { long: string; short: string }> = {
  oauth: { long: 'Signed in', short: 'signed in' },
  token: { long: 'Personal token', short: 'token' },
};

/** "Signed in" or "Personal token"; the admins' table uses the short form. */
export function linkMethodWords(
  method: LinkMethod,
  form: 'long' | 'short' = 'long',
): string {
  return METHOD_WORDS[method][form];
}

/**
 * Whether a link's expiry is the person's to watch: only a pasted token's. A
 * signed-in link's access is renewed by the platform.
 */
export function showsExpiry(link: {
  link_method?: LinkMethod;
  expires_at?: string | null;
}): boolean {
  return linkMethodOf(link) === 'token' && Boolean(link.expires_at);
}

/** What a row of the panel offers (spec 161 research R4). */
export interface RowActions {
  /** The leading button; `null` when a working signed-in link needs none. */
  main: 'connect' | 'link' | 'replace' | 'relink' | null;
  /** "Use a personal access token instead", beside Connect. */
  tokenInstead: boolean;
  unlink: boolean;
}

/**
 * Signing in comes first wherever it is available — to link, and to relink a
 * link that stopped working, however it was made. A working token is left to
 * be replaced as before; a working signed-in link has nothing to replace.
 */
export function rowActions(row: AccountRow): RowActions {
  const { account, oauthAvailable } = row;
  if (!account) {
    return oauthAvailable
      ? { main: 'connect', tokenInstead: true, unlink: false }
      : { main: 'link', tokenInstead: false, unlink: false };
  }
  if (account.status !== 'working') {
    return oauthAvailable
      ? { main: 'connect', tokenInstead: true, unlink: true }
      : { main: 'relink', tokenInstead: false, unlink: true };
  }
  return linkMethodOf(account) === 'oauth'
    ? { main: null, tokenInstead: false, unlink: true }
    : { main: 'replace', tokenInstead: false, unlink: true };
}

/** Where the forge creates a personal token, by its URL convention. */
export function tokenPageUrl(provider: ForgeProvider, host: string): string {
  if (provider === 'gitlab') {
    return `https://${host}/-/user_settings/personal_access_tokens?name=AI-Powered%20SDLC&scopes=api,write_repository`;
  }
  return `https://${host}/settings/tokens`;
}

/** What the token must be allowed to do, in the forge's own words. */
export function requiredPermissions(provider: ForgeProvider): string {
  return provider === 'gitlab'
    ? 'Needs the api and write_repository scopes.'
    : 'Needs repo (classic token), or a fine-grained token with Contents and Pull requests: read and write.';
}

export type LinkRefusalCategory =
  'token_rejected' | 'missing_scopes' | 'unknown_host';

/**
 * Why the service refused a token, from its 422 body.
 *
 * Read defensively, as spec 158's commit-email refusal is: the generated type
 * for this status says nothing, and the service answers with its violations
 * envelope, `{ violations: [{ path: 'token', category, message }] }`.
 */
export function linkRefusal(
  body: unknown,
): { category: LinkRefusalCategory; message: string } | null {
  if (typeof body !== 'object' || body === null || !('violations' in body)) {
    return null;
  }
  const { violations } = body;
  if (!Array.isArray(violations)) return null;
  for (const violation of violations as unknown[]) {
    if (typeof violation !== 'object' || violation === null) continue;
    const { path, category, message } = violation as {
      path?: unknown;
      category?: unknown;
      message?: unknown;
    };
    if (
      path === 'token' &&
      typeof message === 'string' &&
      (category === 'token_rejected' ||
        category === 'missing_scopes' ||
        category === 'unknown_host')
    ) {
      return { category, message };
    }
  }
  return null;
}

/**
 * The forge commit email of the person's first working link — GitLab before
 * GitHub, as the service chooses (spec 139 R10) — or `null`.
 */
export function linkedCommitEmail(
  accounts: readonly ForgeAccountView[],
): { email: string; provider: ForgeProvider } | null {
  const working = accounts
    .filter((account) => account.status === 'working')
    .filter((account) => account.forge_commit_email)
    .sort(
      (a, b) =>
        Number(a.provider !== 'gitlab') - Number(b.provider !== 'gitlab') ||
        a.host.localeCompare(b.host),
    );
  const first = working[0];
  return first?.forge_commit_email
    ? { email: first.forge_commit_email, provider: first.provider }
    : null;
}

/** Who commits every agent commit (spec 138): the platform, never a person. */
export const PLATFORM_COMMITTER = 'AI-Powered SDLC Agent';

export type PushedAs =
  | { kind: 'personal'; username: string }
  | { kind: 'shared'; reason: 'not-linked' | 'relink' };

/**
 * Whose name a push to this host carries (spec 163 US1): the person's, when
 * their link works; the workspace's shared account otherwise, and why.
 */
export function pushedAs(account: ForgeAccountView | null): PushedAs {
  if (!account) return { kind: 'shared', reason: 'not-linked' };
  if (account.status !== 'working') return { kind: 'shared', reason: 'relink' };
  return { kind: 'personal', username: account.forge_username };
}

export interface HostUsage {
  /** How many workspaces connect to the host. */
  count: number;
  /** The names of those that require each person's own account, sorted. */
  required: string[];
  /** Workspaces that require an account but whose name is not known here. */
  requiredUnnamed: number;
}

/**
 * Where a host is used and required (spec 142, 163 US2), named from the
 * workspace list the app already reads.
 */
export function hostUsage(
  host: Pick<ForgeHostView, 'workspace_ids' | 'required_workspace_ids'>,
  names: ReadonlyMap<string, string>,
): HostUsage {
  const required = host.required_workspace_ids ?? [];
  const named = required
    .map((id) => names.get(id))
    .filter((name): name is string => name !== undefined)
    .sort((a, b) => a.localeCompare(b));
  return {
    count: (host.workspace_ids ?? []).length,
    required: named,
    requiredUnnamed: required.length - named.length,
  };
}

/**
 * Whether a working token can do what runs need, in place of the scope list
 * (spec 163 US2). A signed-in link asked for its scopes when it was made;
 * `null` then, and for a link that does not work.
 */
export function permissionsKnown(account: ForgeAccountView): boolean | null {
  if (account.status !== 'working') return null;
  if (linkMethodOf(account) === 'oauth') return null;
  return !account.scopes_unknown;
}

/** The id of a host's account card, so the preview can bring it into view (spec 170). */
export function accountCardId(host: string): string {
  return `linked-account-${host.replace(/[^a-z0-9-]/gi, '-')}`;
}
